import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ExitButtonComponent } from '../../../../shared/components/buttons/exit-button/exit-button.component';
import { RideAlongService } from '../../../../services/ride-along.service';
import { TripService } from '../../../../services/trip.service';
import { ToastService } from '../../../../services/toast.service';
import { RideAlongTrip } from '../../models/ride-along.model';

type InviteTab = 'find' | 'sent' | 'on_trip';

/**
 * Invite a Mate sheet — captain's ride-along management UI. Web mirror of the
 * Flutter invite_a_mate_sheet.dart with Find / Sent / On Trip tabs:
 *   - Find: dockmates to invite (Invite / status)
 *   - Sent: pending invitations the captain sent (Cancel)
 *   - On Trip: active participants (Remove)
 */
@Component({
  selector: 'app-invite-a-mate',
  standalone: true,
  imports: [CommonModule, FormsModule, ExitButtonComponent],
  templateUrl: './invite-a-mate.component.html',
  styleUrl: './invite-a-mate.component.scss',
})
export class InviteAMateComponent implements OnInit, OnDestroy {
  @Input() tripId = '';
  /** Trip privacy — ride-along only works for public/custom trips. */
  @Input() tripPrivacy = '';
  @Output() close = new EventEmitter<void>();

  tab: InviteTab = 'find';
  rows: RideAlongTrip[] = [];
  loading = false;
  search = '';
  /** Ids currently mid-action so their button shows a busy/disabled state. */
  busyIds = new Set<string>();

  private searchInput$ = new Subject<string>();
  private subs = new Subscription();
  private privacyResolved = false;

  constructor(
    private rideAlongService: RideAlongService,
    private tripService: TripService,
    private toast: ToastService,
  ) {}

  get isRideAlongTrip(): boolean {
    const p = (this.tripPrivacy || '').toLowerCase();
    return p === 'public' || p === 'custom' || p === 'dockmates';
  }

  ngOnInit(): void {
    this.subs.add(
      this.searchInput$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((term) => {
        this.search = term;
        this.load();
      }),
    );
    // Resolve privacy from the trip if the caller didn't provide it.
    if (!this.tripPrivacy && this.tripId) {
      this.tripService.getTripById(this.tripId).subscribe({
        next: (trip: any) => {
          this.tripPrivacy = trip?.privacy || '';
          this.privacyResolved = true;
          if (this.isRideAlongTrip) this.load();
        },
        error: () => {
          this.privacyResolved = true;
        },
      });
    } else if (this.isRideAlongTrip) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  setTab(tab: InviteTab): void {
    if (this.tab === tab) return;
    this.tab = tab;
    this.search = '';
    this.load();
  }

  onSearchChange(term: string): void {
    this.searchInput$.next(term);
  }

  onClose(): void {
    this.close.emit();
  }

  load(): void {
    if (!this.tripId || !this.isRideAlongTrip) return;
    this.loading = true;
    this.rideAlongService.discoverRideAlong(this.tab, this.tripId, this.search).subscribe({
      next: (rows) => {
        this.rows = rows;
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
        this.toast.error('Could not load mates.');
      },
    });
  }

  invite(row: RideAlongTrip): void {
    if (this.busyIds.has(row.keycloakId)) return;
    this.busyIds.add(row.keycloakId);
    this.rideAlongService.inviteRideAlong(this.tripId, row.keycloakId).subscribe({
      next: () => {
        this.busyIds.delete(row.keycloakId);
        row.inviteStatus = 'invited';
        this.toast.success(`Invited ${row.displayName}.`);
      },
      error: () => {
        this.busyIds.delete(row.keycloakId);
        this.toast.error('Could not send invite.');
      },
    });
  }

  cancel(row: RideAlongTrip): void {
    if (!row.requestId || this.busyIds.has(row.requestId)) return;
    this.busyIds.add(row.requestId);
    this.rideAlongService.cancelRideAlongRequest(this.tripId, row.requestId).subscribe({
      next: () => {
        this.busyIds.delete(row.requestId!);
        this.rows = this.rows.filter((r) => r.requestId !== row.requestId);
        this.toast.success('Invitation cancelled.');
      },
      error: () => {
        this.busyIds.delete(row.requestId!);
        this.toast.error('Could not cancel invite.');
      },
    });
  }

  remove(row: RideAlongTrip): void {
    if (!row.keycloakId || this.busyIds.has(row.keycloakId)) return;
    this.busyIds.add(row.keycloakId);
    this.rideAlongService.removeRideAlongParticipant(this.tripId, row.keycloakId).subscribe({
      next: () => {
        this.busyIds.delete(row.keycloakId);
        this.rows = this.rows.filter((r) => r.keycloakId !== row.keycloakId);
        this.toast.success(`Removed ${row.displayName}.`);
      },
      error: () => {
        this.busyIds.delete(row.keycloakId);
        this.toast.error('Could not remove mate.');
      },
    });
  }

  initial(name: string): string {
    return (name || '?').charAt(0).toUpperCase();
  }
}
